// Shared UI kit (integrator-owned). Feature folders compose these; if a feature needs a
// new shared primitive, build it locally in the feature first and propose it here.
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'
import { useEffect, useId } from 'react'
import { Link } from 'react-router-dom'

export type Tone = 'neutral' | 'info' | 'ok' | 'warn' | 'bad' | 'brand' | 'synthetic'

const toneClasses: Record<Tone, string> = {
  neutral: 'bg-paper text-ink-soft border-line',
  info: 'bg-info-light text-info border-info/30',
  ok: 'bg-ok-light text-ok border-ok/30',
  warn: 'bg-warn-light text-warn border-warn/30',
  bad: 'bg-bad-light text-bad border-bad/30',
  brand: 'bg-brand-light text-brand-dark border-brand/30',
  synthetic: 'bg-synthetic-light text-synthetic border-synthetic/30',
}

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ')
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost'
  loading?: boolean
  block?: boolean
}

export function Button({ variant = 'primary', loading, block, className, children, disabled, ...rest }: ButtonProps) {
  const base = 'inline-flex items-center justify-center gap-2 rounded-lg px-4 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed'
  const styles = {
    primary: 'bg-brand text-white hover:bg-brand-dark',
    secondary: 'bg-card text-brand-dark border border-line-strong hover:bg-brand-light',
    danger: 'bg-bad text-white hover:brightness-95',
    ghost: 'bg-transparent text-brand-dark hover:bg-brand-light',
  }[variant]
  return (
    <button className={cx(base, styles, block && 'w-full', className)} disabled={disabled || loading} {...rest}>
      {loading ? <Spinner small /> : null}
      {children}
    </button>
  )
}

/** A router link that looks like a Button (never a button nested inside a link). */
export function LinkButton({ to, variant = 'primary', className, children }: {
  to: string
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost'
  className?: string
  children: ReactNode
}) {
  const styles = {
    primary: 'bg-brand text-white hover:bg-brand-dark',
    secondary: 'bg-card text-brand-dark border border-line-strong hover:bg-brand-light',
    danger: 'bg-bad text-white hover:brightness-95',
    ghost: 'bg-transparent text-brand-dark hover:bg-brand-light',
  }[variant]
  return (
    <Link to={to} className={cx('tap inline-flex items-center justify-center gap-2 rounded-lg px-4 font-semibold transition', styles, className)}>
      {children}
    </Link>
  )
}

export function Spinner({ small }: { small?: boolean }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cx('inline-block animate-spin rounded-full border-2 border-current border-t-transparent', small ? 'h-4 w-4' : 'h-6 w-6')}
    />
  )
}

export function Card({ children, className, accent }: { children: ReactNode; className?: string; accent?: 'class-b' | 'class-a' | 'synthetic' }) {
  const accentClass = accent === 'class-b' ? 'border-t-4 border-t-class-b' : accent === 'class-a' ? 'border-t-4 border-t-class-a' : accent === 'synthetic' ? 'border-t-4 border-t-synthetic' : ''
  // White 12px panel with restrained depth (visual refresh); padding grows on wider screens.
  return <div className={cx('rounded-xl border border-line bg-card p-4 shadow-[0_4px_18px_rgb(33_59_87/5%)] sm:p-5', accentClass, className)}>{children}</div>
}

export function SectionTitle({ children, aside, no }: { children: ReactNode; aside?: ReactNode; no?: number }) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
      <h2 className="display text-[19px] leading-snug font-semibold text-[#354b64]">
        {no ? <span className="mr-2 text-ink-faint">{no}.</span> : null}
        {children}
      </h2>
      {aside}
    </div>
  )
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="va-page-heading mb-6 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="display">{title}</h1>
        {subtitle ? <p className="mt-2">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  )
}

export function Badge({ tone = 'neutral', children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return (
    <span className={cx('inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold', toneClasses[tone], className)}>
      {children}
    </span>
  )
}

export function Banner({ tone = 'info', title, children, className }: { tone?: Tone; title?: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <div role={tone === 'bad' ? 'alert' : 'status'} className={cx('rounded-lg border p-3 text-sm leading-relaxed sm:px-4', toneClasses[tone], className)}>
      {title ? <p className="font-semibold">{title}</p> : null}
      {children ? <div className={cx(title ? 'mt-1' : '', 'text-ink')}>{children}</div> : null}
    </div>
  )
}

export function Field({ label, hint, error, required, children, htmlFor }: {
  label: ReactNode
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  children: ReactNode
  htmlFor?: string
}) {
  return (
    <div className="mb-4">
      <label htmlFor={htmlFor} className="mb-1 block text-sm font-semibold text-ink">
        {label}
        {required ? <span className="ml-1 text-bad" aria-hidden>*</span> : null}
      </label>
      {children}
      {hint ? <p className="mt-1 text-[13px] leading-normal text-ink-soft">{hint}</p> : null}
      {error ? <p className="mt-1 text-[13px] leading-normal font-semibold text-bad">{error}</p> : null}
    </div>
  )
}

const inputClass = 'w-full min-h-[46px] rounded-lg border border-line-strong bg-white px-3 py-2 leading-normal text-ink placeholder:text-ink-faint'

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cx(inputClass, props.className)} />
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea rows={3} {...props} className={cx(inputClass, 'min-h-[88px]', props.className)} />
}

export function Select({ options, placeholder, ...props }: SelectHTMLAttributes<HTMLSelectElement> & {
  options: readonly { value: string; label: string }[]
  placeholder?: string
}) {
  return (
    <select {...props} className={cx(inputClass, props.className)}>
      {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

export function Checkbox({ checked, onChange, label, hint, disabled }: {
  checked: boolean
  onChange: (v: boolean) => void
  label: ReactNode
  hint?: ReactNode
  disabled?: boolean
}) {
  const id = useId()
  // The whole row is the label, so the tap target is at least 44px tall (gloves).
  return (
    <label htmlFor={id} className={cx('flex min-h-[44px] cursor-pointer items-start gap-3 py-3', disabled && 'cursor-not-allowed opacity-60')}>
      <input id={id} type="checkbox" className="mt-0.5 shrink-0" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-sm text-ink">
        {label}
        {hint ? <span className="mt-0.5 block text-xs text-ink-soft">{hint}</span> : null}
      </span>
    </label>
  )
}

/** A list of yes/no confirmations; every item must be ticked deliberately. */
export function CheckList({ options, value, onChange, disabled }: {
  options: readonly { code: string; label: string; help?: string }[]
  value: Record<string, boolean>
  onChange: (next: Record<string, boolean>) => void
  disabled?: boolean
}) {
  return (
    <div className="divide-y divide-line rounded-lg border border-line bg-white px-3">
      {options.map((o) => (
        <Checkbox
          key={o.code}
          checked={Boolean(value[o.code])}
          disabled={disabled}
          onChange={(v) => onChange({ ...value, [o.code]: v })}
          label={o.label}
          hint={o.help}
        />
      ))}
    </div>
  )
}

export function RadioGroup<T extends string>({ name, options, value, onChange }: {
  name: string
  options: readonly { value: T; label: ReactNode; hint?: ReactNode; disabled?: boolean }[]
  value: T | null
  onChange: (v: T) => void
}) {
  return (
    <div className="space-y-2">
      {options.map((o) => (
        <label
          key={o.value}
          className={cx(
            'flex cursor-pointer items-start gap-3 rounded-lg border p-3',
            value === o.value ? 'border-brand bg-brand-light' : 'border-line bg-white',
            o.disabled && 'cursor-not-allowed opacity-60',
          )}
        >
          <input type="radio" name={name} className="mt-0.5" checked={value === o.value} disabled={o.disabled} onChange={() => onChange(o.value)} />
          <span className="text-sm">
            <span className="font-semibold text-ink">{o.label}</span>
            {o.hint ? <span className="mt-0.5 block text-xs text-ink-soft">{o.hint}</span> : null}
          </span>
        </label>
      ))}
    </div>
  )
}

export function KeyValue({ items, cols = 2 }: { items: Array<[ReactNode, ReactNode]>; cols?: 1 | 2 | 3 }) {
  return (
    <dl className={cx('grid gap-x-6 gap-y-3', cols === 1 ? 'grid-cols-1' : cols === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-3')}>
      {items.map(([k, v], i) => (
        <div key={i}>
          <dt className="text-xs font-semibold uppercase tracking-wide text-ink-faint">{k}</dt>
          <dd className="mt-0.5 text-sm text-ink">{v ?? '-'}</dd>
        </div>
      ))}
    </dl>
  )
}

export function EmptyState({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-line-strong bg-white p-6 text-center">
      <p className="font-semibold text-ink">{title}</p>
      {children ? <div className="mt-1 text-sm text-ink-soft">{children}</div> : null}
    </div>
  )
}

/** Bottom sheet on phones, centred dialog on wider screens. */
export function Sheet({ open, title, onClose, children }: { open: boolean; title: ReactNode; onClose: () => void; children: ReactNode }) {
  const titleId = useId()
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-ink/40 sm:items-center" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="max-h-[92dvh] w-full overflow-y-auto rounded-t-2xl bg-paper p-4 shadow-xl sm:max-w-2xl sm:rounded-2xl">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 id={titleId} className="display text-xl font-bold text-ink">{title}</h2>
          <Button variant="ghost" onClick={onClose} aria-label="Close">
            Close
          </Button>
        </div>
        {children}
      </div>
    </div>
  )
}
